import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  FormHelperText,
  Tooltip,
  Icon,
  IconButton,
  useDisclosure,
  Box,
  Text,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faPlus, faGlobe, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { sitesListAtom } from '../../state/sitesAtom';
import { sitesApi } from '../../api/sites';
import SiteModal from '../sites/SiteModal';

// Helper component for labels with info tooltip
function LabelWithTooltip({ children, tooltip }) {
  return (
    <HStack spacing={1} display="inline-flex">
      <span>{children}</span>
      {tooltip && (
        <Tooltip label={tooltip} hasArrow placement="top" fontSize="xs">
          <span>
            <Icon as={FontAwesomeIcon} icon={faInfoCircle} boxSize={3} color="gray.400" cursor="help" />
          </span>
        </Tooltip>
      )}
    </HStack>
  );
}

const initialFormData = {
  name: '',
  siteId: '',
  width: 300,
  height: 250,
  description: '',
  zoneType: 'web',
  rotationType: 'onload',
  rotationInterval: 30,
  // Email-specific fields
  emailProvider: '',
  campaignName: '',
};

function ZoneModal({ isOpen, onClose, onSubmit, zone, isLoading }) {
  const [formData, setFormData] = useState(initialFormData);
  const sites = useRecoilValue(sitesListAtom);
  const setSitesList = useSetRecoilState(sitesListAtom);
  const toast = useToast();
  const { isOpen: isSiteModalOpen, onOpen: onSiteModalOpen, onClose: onSiteModalClose } = useDisclosure();
  const [siteLoading, setSiteLoading] = useState(false);
  const isEditing = !!zone;

  const isEmailZone = formData.zoneType === 'email';

  const typeSelectorBg = useColorModeValue('gray.50', 'gray.700');
  const typeSelectedBg = useColorModeValue('white', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleCreateSite = async (data) => {
    setSiteLoading(true);
    try {
      const response = await sitesApi.create(data);
      if (response.data) {
        setSitesList(prev => [...(prev || []), response.data]);
        toast({
          title: 'Site created',
          description: `${data.name} has been added successfully.`,
          status: 'success',
          duration: 3000,
        });
        setFormData(prev => ({ ...prev, siteId: response.data.site_id }));
        onSiteModalClose();
      }
    } catch (error) {
      toast({
        title: 'Failed to create site',
        description: error.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSiteLoading(false);
    }
  };

  useEffect(() => {
    if (zone) {
      setFormData({
        name: zone.zone_name || zone.name || '',
        siteId: zone.site_id || '',
        width: zone.width || 300,
        height: zone.height || 250,
        description: zone.zone_description || zone.description || '',
        zoneType: zone.zone_type || 'web',
        rotationType: zone.rotation_type || 'onload',
        rotationInterval: zone.rotation_interval || 30,
        emailProvider: zone.email_provider || '',
        campaignName: zone.campaign_name || '',
      });
    } else {
      setFormData(initialFormData);
    }
  }, [zone, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
  };

  const handleZoneTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      zoneType: type,
      // Reset type-specific fields
      ...(type === 'email' ? { rotationType: 'none', siteId: '' } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: 'Zone name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Site is only required for web zones
    if (!isEmailZone && !formData.siteId) {
      toast({
        title: 'Please select a site',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    await onSubmit({
      name: formData.name,
      siteId: isEmailZone ? null : parseInt(formData.siteId),
      width: formData.width,
      height: formData.height,
      description: formData.description,
      zoneType: formData.zoneType,
      rotationType: isEmailZone ? 'none' : formData.rotationType,
      rotationInterval: isEmailZone ? 0 : formData.rotationInterval,
      emailProvider: isEmailZone ? formData.emailProvider : null,
      campaignName: isEmailZone ? formData.campaignName : null,
    });
  };

  // Common ad sizes for quick selection
  const webSizes = [
    { label: 'Medium Rectangle', width: 300, height: 250 },
    { label: 'Leaderboard', width: 728, height: 90 },
    { label: 'Wide Skyscraper', width: 160, height: 600 },
    { label: 'Half Page', width: 300, height: 600 },
    { label: 'Large Rectangle', width: 336, height: 280 },
    { label: 'Mobile Banner', width: 320, height: 50 },
  ];

  const emailSizes = [
    { label: 'Full Width', width: 600, height: 100 },
    { label: 'Leaderboard', width: 600, height: 90 },
    { label: 'Medium Banner', width: 468, height: 60 },
    { label: 'Square', width: 300, height: 250 },
    { label: 'Small Banner', width: 300, height: 100 },
  ];

  const zoneSizes = isEmailZone ? emailSizes : webSizes;

  const handleSizePreset = (width, height) => {
    setFormData(prev => ({ ...prev, width, height }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>{isEditing ? 'Edit Zone' : 'Create New Zone'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* Zone Type Selector - Prominent at top */}
            <Box mb={6}>
              <FormLabel mb={3}>
                <LabelWithTooltip tooltip="Website zones display ads on web pages. Email zones track ads in HTML email campaigns.">
                  Zone Type
                </LabelWithTooltip>
              </FormLabel>
              <HStack
                spacing={0}
                p={1}
                bg={typeSelectorBg}
                borderRadius="xl"
                border="1px solid"
                borderColor={borderColor}
              >
                <Box
                  flex={1}
                  py={3}
                  px={4}
                  textAlign="center"
                  cursor="pointer"
                  borderRadius="lg"
                  bg={!isEmailZone ? typeSelectedBg : 'transparent'}
                  boxShadow={!isEmailZone ? 'sm' : 'none'}
                  onClick={() => handleZoneTypeChange('web')}
                  transition="all 0.2s"
                >
                  <HStack justify="center" spacing={2}>
                    <Icon
                      as={FontAwesomeIcon}
                      icon={faGlobe}
                      color={!isEmailZone ? 'blue.500' : 'gray.400'}
                    />
                    <Text fontWeight={!isEmailZone ? '600' : '400'} color={!isEmailZone ? 'blue.500' : 'gray.500'}>
                      Website
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Ads on web pages
                  </Text>
                </Box>
                <Box
                  flex={1}
                  py={3}
                  px={4}
                  textAlign="center"
                  cursor="pointer"
                  borderRadius="lg"
                  bg={isEmailZone ? typeSelectedBg : 'transparent'}
                  boxShadow={isEmailZone ? 'sm' : 'none'}
                  onClick={() => handleZoneTypeChange('email')}
                  transition="all 0.2s"
                >
                  <HStack justify="center" spacing={2}>
                    <Icon
                      as={FontAwesomeIcon}
                      icon={faEnvelope}
                      color={isEmailZone ? 'purple.500' : 'gray.400'}
                    />
                    <Text fontWeight={isEmailZone ? '600' : '400'} color={isEmailZone ? 'purple.500' : 'gray.500'}>
                      Email Campaign
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Ads in HTML emails
                  </Text>
                </Box>
              </HStack>
            </Box>

            <Divider mb={6} />

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {/* Left Column */}
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>
                    <LabelWithTooltip tooltip={isEmailZone ? "A name for this email ad placement" : "A unique name to identify this ad placement"}>
                      {isEmailZone ? 'Zone Name' : 'Zone Name'}
                    </LabelWithTooltip>
                  </FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={isEmailZone ? "e.g., Newsletter Header Banner" : "e.g., Homepage Leaderboard"}
                    borderRadius="xl"
                  />
                </FormControl>

                {/* Web-only: Site Selection */}
                {!isEmailZone && (
                  <FormControl isRequired>
                    <FormLabel>
                      <LabelWithTooltip tooltip="The website where this zone will display ads">
                        Site
                      </LabelWithTooltip>
                    </FormLabel>
                    <HStack spacing={2}>
                      <Select
                        name="siteId"
                        value={formData.siteId}
                        onChange={handleChange}
                        placeholder="Select site"
                        borderRadius="xl"
                        flex={1}
                      >
                        {sites?.map(s => (
                          <option key={s.site_id || s.id} value={s.site_id || s.id}>
                            {s.site_name || s.name}
                          </option>
                        ))}
                      </Select>
                      <Tooltip label="Create new site" hasArrow>
                        <IconButton
                          icon={<FontAwesomeIcon icon={faPlus} />}
                          colorScheme="brand"
                          variant="outline"
                          borderRadius="xl"
                          onClick={onSiteModalOpen}
                          aria-label="Create new site"
                        />
                      </Tooltip>
                    </HStack>
                  </FormControl>
                )}

                {/* Email-only: Provider & Campaign Name */}
                {isEmailZone && (
                  <>
                    <FormControl>
                      <FormLabel>
                        <LabelWithTooltip tooltip="The email service you're using to send campaigns">
                          Email Provider
                        </LabelWithTooltip>
                      </FormLabel>
                      <Select
                        name="emailProvider"
                        value={formData.emailProvider}
                        onChange={handleChange}
                        placeholder="Select provider (optional)"
                        borderRadius="xl"
                      >
                        <option value="mailchimp">Mailchimp</option>
                        <option value="sendgrid">SendGrid</option>
                        <option value="constant_contact">Constant Contact</option>
                        <option value="campaign_monitor">Campaign Monitor</option>
                        <option value="klaviyo">Klaviyo</option>
                        <option value="hubspot">HubSpot</option>
                        <option value="outlook">Outlook/Office 365</option>
                        <option value="other">Other</option>
                      </Select>
                      <FormHelperText>Helps organize your email zones</FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>
                        <LabelWithTooltip tooltip="Optional campaign or newsletter name for reference">
                          Campaign Name
                        </LabelWithTooltip>
                      </FormLabel>
                      <Input
                        name="campaignName"
                        value={formData.campaignName}
                        onChange={handleChange}
                        placeholder="e.g., Weekly Newsletter, Promo Blast"
                        borderRadius="xl"
                      />
                      <FormHelperText>Optional - for your reference</FormHelperText>
                    </FormControl>
                  </>
                )}

                {/* Web-only: Rotation Settings */}
                {!isEmailZone && (
                  <>
                    <FormControl>
                      <FormLabel>
                        <LabelWithTooltip tooltip="No Rotation shows a fixed ad. On Load shows one ad per page load. Timed rotates ads automatically.">
                          Ad Rotation
                        </LabelWithTooltip>
                      </FormLabel>
                      <Select
                        name="rotationType"
                        value={formData.rotationType}
                        onChange={handleChange}
                        borderRadius="xl"
                      >
                        <option value="none">No Rotation (fixed ad)</option>
                        <option value="onload">On Load (single ad per page view)</option>
                        <option value="timed">Timed Rotation (auto-refresh)</option>
                      </Select>
                      <FormHelperText>
                        {formData.rotationType === 'timed'
                          ? 'Ads will rotate automatically while the page is open'
                          : formData.rotationType === 'none'
                          ? 'Shows a fixed ad without any rotation'
                          : 'One ad per page load, different ad on refresh'}
                      </FormHelperText>
                    </FormControl>

                    {formData.rotationType === 'timed' && (
                      <FormControl>
                        <FormLabel>
                          <LabelWithTooltip tooltip="How often (in seconds) to rotate to a new ad">
                            Rotation Interval
                          </LabelWithTooltip>
                        </FormLabel>
                        <NumberInput
                          value={formData.rotationInterval}
                          onChange={(val) => handleNumberChange('rotationInterval', val)}
                          min={5}
                          max={300}
                        >
                          <NumberInputField borderRadius="xl" />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        <FormHelperText>Seconds between ad rotations (5-300)</FormHelperText>
                      </FormControl>
                    )}
                  </>
                )}

                {/* Email-only: Info Box */}
                {isEmailZone && (
                  <Box p={4} bg="purple.50" borderRadius="xl" _dark={{ bg: 'purple.900' }}>
                    <Text fontSize="sm" color="purple.700" _dark={{ color: 'purple.200' }}>
                      <strong>How email tracking works:</strong>
                    </Text>
                    <Text fontSize="xs" color="purple.600" mt={1} _dark={{ color: 'purple.300' }}>
                      Impressions are tracked when recipients open the email and load the ad image.
                      Clicks are tracked when they click the ad link. Stats accumulate over time
                      as more recipients open your email.
                    </Text>
                  </Box>
                )}

                <FormControl>
                  <FormLabel>
                    <LabelWithTooltip tooltip="Optional notes about this zone">
                      Description
                    </LabelWithTooltip>
                  </FormLabel>
                  <Input
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Optional description"
                    borderRadius="xl"
                  />
                </FormControl>
              </VStack>

              {/* Right Column - Size Selection */}
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>
                    <LabelWithTooltip tooltip={isEmailZone ? "Common email banner sizes. Most emails are 600px wide max." : "Standard IAB ad sizes."}>
                      {isEmailZone ? 'Banner Size (Email Safe)' : 'Zone Size (IAB Presets)'}
                    </LabelWithTooltip>
                  </FormLabel>
                  <HStack spacing={2} mb={2} flexWrap="wrap">
                    {zoneSizes.map(size => (
                      <Button
                        key={size.label}
                        size="xs"
                        variant={formData.width === size.width && formData.height === size.height ? 'solid' : 'outline'}
                        colorScheme={isEmailZone ? 'purple' : 'brand'}
                        onClick={() => handleSizePreset(size.width, size.height)}
                        borderRadius="full"
                        mb={1}
                      >
                        {size.label}
                      </Button>
                    ))}
                  </HStack>
                  <SimpleGrid columns={2} spacing={4}>
                    <NumberInput
                      value={formData.width}
                      onChange={(val) => handleNumberChange('width', val)}
                      min={1}
                    >
                      <NumberInputField borderRadius="xl" placeholder="Width" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <NumberInput
                      value={formData.height}
                      onChange={(val) => handleNumberChange('height', val)}
                      min={1}
                    >
                      <NumberInputField borderRadius="xl" placeholder="Height" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </SimpleGrid>
                  <FormHelperText>
                    {isEmailZone
                      ? 'Email banners should be max 600px wide for best compatibility'
                      : 'Select a preset or enter custom dimensions'}
                  </FormHelperText>
                </FormControl>

                {/* Preview Box */}
                <Box
                  mt={4}
                  p={4}
                  border="2px dashed"
                  borderColor={borderColor}
                  borderRadius="xl"
                  textAlign="center"
                >
                  <Text fontSize="xs" color="gray.500" mb={2}>Preview Size</Text>
                  <Box
                    mx="auto"
                    bg={isEmailZone ? 'purple.100' : 'brand.100'}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      width: Math.min(formData.width, 250),
                      height: Math.min(formData.height, 150),
                      aspectRatio: `${formData.width} / ${formData.height}`,
                    }}
                    _dark={{ bg: isEmailZone ? 'purple.800' : 'brand.800' }}
                  >
                    <Text fontSize="xs" color={isEmailZone ? 'purple.600' : 'brand.600'} _dark={{ color: isEmailZone ? 'purple.200' : 'brand.200' }}>
                      {formData.width} x {formData.height}
                    </Text>
                  </Box>
                </Box>
              </VStack>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose} borderRadius="xl">
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme={isEmailZone ? 'purple' : 'brand'}
              isLoading={isLoading}
              borderRadius="xl"
            >
              {isEditing ? 'Save Changes' : `Create ${isEmailZone ? 'Email' : 'Web'} Zone`}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>

      {/* Nested Site Modal */}
      <SiteModal
        isOpen={isSiteModalOpen}
        onClose={onSiteModalClose}
        onSubmit={handleCreateSite}
        isLoading={siteLoading}
      />
    </Modal>
  );
}

export default ZoneModal;
